import * as Phaser from "phaser";

export type AsteroidSize = "large" | "medium" | "small";

export const ASTEROID_RADII: Record<AsteroidSize, number> = {
  large: 46,
  medium: 24,
  small: 12,
};

export const ASTEROID_SCORES: Record<AsteroidSize, number> = {
  large: 20,
  medium: 50,
  small: 100,
};

export function splitSize(size: AsteroidSize): AsteroidSize | null {
  if (size === "large") return "medium";
  if (size === "medium") return "small";
  return null;
}

export class Ship {
  x: number;
  y: number;
  vx = 0;
  vy = 0;
  rotation = -Math.PI / 2;
  radius = 7;
  alive = true;
  invulnUntil = 0;
  thrusting = false;
  reversing = false;
  tractorOn = false;
  strokeColor = 0x44ff77;
  flareColor = 0x44ff77;
  readonly gfx: Phaser.GameObjects.Graphics;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    this.x = x;
    this.y = y;
    this.gfx = scene.add.graphics();
    this.gfx.setScale(0.7); // shrink to match smaller hitbox
    this.redraw();
  }

  redraw(): void {
    const g = this.gfx;
    g.clear();
    if (!this.alive) return;
    g.lineStyle(2, this.strokeColor, 1);
    g.beginPath();
    g.moveTo(14, 0);
    g.lineTo(-10, -9);
    g.lineTo(-6, 0);
    g.lineTo(-10, 9);
    g.closePath();
    g.strokePath();
    if (this.thrusting) {
      g.lineStyle(2, this.flareColor, 1);
      g.beginPath();
      g.moveTo(-7, -4);
      g.lineTo(-16, 0);
      g.lineTo(-7, 4);
      g.strokePath();
    }
    if (this.reversing) {
      g.lineStyle(2, 0x44ff77, 1);
      g.beginPath();
      g.moveTo(11, -3);
      g.lineTo(18, 0);
      g.lineTo(11, 3);
      g.strokePath();
    }
  }

  syncView(now: number): void {
    this.gfx.x = this.x;
    this.gfx.y = this.y;
    this.gfx.rotation = this.rotation;
    if (this.alive && now < this.invulnUntil) {
      this.gfx.alpha = Math.floor(now / 80) % 2 === 0 ? 0.3 : 1;
    } else {
      this.gfx.alpha = this.alive ? 1 : 0;
    }
  }

  destroy(): void {
    this.gfx.destroy();
  }
}

export class Bullet {
  x: number;
  y: number;
  vx: number;
  vy: number;
  expiresAt: number;
  hitsRemaining = 1;
  homing = false;
  readonly radius = 2;
  readonly gfx: Phaser.GameObjects.Graphics;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    vx: number,
    vy: number,
    expiresAt: number,
    color = 0xffffff,
  ) {
    this.x = x;
    this.y = y;
    this.vx = vx;
    this.vy = vy;
    this.expiresAt = expiresAt;
    this.gfx = scene.add.graphics();
    this.gfx.fillStyle(color, 1);
    this.gfx.fillRect(-2, -2, 4, 4);
  }

  syncView(): void {
    this.gfx.x = this.x;
    this.gfx.y = this.y;
  }

  destroy(): void {
    this.gfx.destroy();
  }
}

export class Asteroid {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation = 0;
  rotSpeed: number;
  readonly size: AsteroidSize;
  readonly radius: number;
  readonly gfx: Phaser.GameObjects.Graphics;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    vx: number,
    vy: number,
    size: AsteroidSize,
  ) {
    this.x = x;
    this.y = y;
    this.vx = vx;
    this.vy = vy;
    this.size = size;
    this.radius = ASTEROID_RADII[size];
    this.rotSpeed = Phaser.Math.FloatBetween(-0.6, 0.6);

    const n = 10 + Math.floor(Math.random() * 4);
    const verts: { x: number; y: number }[] = [];
    for (let i = 0; i < n; i++) {
      const angle = (i / n) * Math.PI * 2;
      const r = this.radius * Phaser.Math.FloatBetween(0.78, 1.1);
      verts.push({ x: Math.cos(angle) * r, y: Math.sin(angle) * r });
    }
    this.gfx = scene.add.graphics();
    this.gfx.lineStyle(2, 0x22cc55, 1);
    this.gfx.beginPath();
    verts.forEach((v, i) => {
      if (i === 0) this.gfx.moveTo(v.x, v.y);
      else this.gfx.lineTo(v.x, v.y);
    });
    this.gfx.closePath();
    this.gfx.strokePath();
  }

  syncView(): void {
    this.gfx.x = this.x;
    this.gfx.y = this.y;
    this.gfx.rotation = this.rotation;
  }

  destroy(): void {
    this.gfx.destroy();
  }
}

export class Resource {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation = 0;
  readonly rotSpeed: number;
  readonly expiresAt: number;
  readonly radius = 9;
  readonly collectRadius = 18;
  readonly gfx: Phaser.GameObjects.Graphics;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    vx: number,
    vy: number,
    expiresAt: number,
  ) {
    this.x = x;
    this.y = y;
    this.vx = vx;
    this.vy = vy;
    this.rotSpeed = Phaser.Math.FloatBetween(0.7, 1.2);
    this.expiresAt = expiresAt;
    this.gfx = scene.add.graphics();
    const r = this.radius;
    this.gfx.fillStyle(0x22cc55, 1);
    this.gfx.beginPath();
    this.gfx.moveTo(0, -r);
    this.gfx.lineTo(r, 0);
    this.gfx.lineTo(0, r);
    this.gfx.lineTo(-r, 0);
    this.gfx.closePath();
    this.gfx.fillPath();
    this.gfx.lineStyle(1.5, 0x88ffbb, 1);
    this.gfx.beginPath();
    this.gfx.moveTo(0, -r);
    this.gfx.lineTo(r, 0);
    this.gfx.lineTo(0, r);
    this.gfx.lineTo(-r, 0);
    this.gfx.closePath();
    this.gfx.strokePath();
  }

  syncView(_now: number): void {
    this.gfx.x = this.x;
    this.gfx.y = this.y;
    this.gfx.rotation = this.rotation;
    this.gfx.alpha = 1;
  }

  destroy(): void {
    this.gfx.destroy();
  }
}

export function wrap(v: number, max: number): number {
  return ((v % max) + max) % max;
}

export function distance(x1: number, y1: number, x2: number, y2: number): number {
  return Math.hypot(x1 - x2, y1 - y2);
}
